import React, { useState } from 'react';
import { Badge, Button, ConfirmationDialog } from '@roger/ui-components';
import {
  Download,
  FileText,
  Loader2,
  Paperclip,
  Trash2,
  Upload,
} from 'lucide-react';

import {
  downloadPortDocumentFaxNumberPortsPortOrderIdDocumentsDocumentKindGet as downloadPortDocument,
  getLoaTemplateFaxNumberPortsPortOrderIdLoaTemplateGet as getLoaTemplate,
  removePortDocumentFaxNumberPortsPortOrderIdDocumentsDocumentKindDelete as removePortDocument,
  uploadPortDocumentFaxNumberPortsPortOrderIdDocumentsDocumentKindPost as uploadPortDocument,
} from '../../../services/api/sdk.gen';
import type {
  PortDocumentInfo,
  PortOrderDetail,
} from '../../../services/api/types.gen';
import { formatDisplayDateInAgencyZone } from '../../../utils/dateUtils';
import { downloadResponseAsFile } from '../../../utils/downloadFile';
import PdfFileButton from './PdfFileButton';
import SignLoaDialog from './SignLoaDialog';
import {
  describeDownloadError,
  describeError,
  logFailure,
  type Notify,
  pdfProblem,
} from './shared';

type Kind = 'loa' | 'invoice';

const LABELS: Record<Kind, string> = {
  loa: 'Signed authorization letter',
  invoice: 'Recent carrier invoice',
};

const REMOVE_MESSAGES: Record<Kind, string> = {
  loa: 'You cannot submit until you upload another.',
  invoice:
    'Your signed letter is sent in its place. Details filled from it stay.',
};

const loaLine = (detail: PortOrderDetail, loa: PortDocumentInfo): string => {
  if (!loa.attached) {
    return detail.documents.loa_resign_reason
      ? 'Not attached. The earlier signature was removed.'
      : 'Not attached';
  }
  if (detail.submitted_at) {
    return `${loa.filename ?? 'Letter'} · Sent ${formatDisplayDateInAgencyZone(detail.submitted_at)}`;
  }
  const date = formatDisplayDateInAgencyZone(loa.attached_at);
  if (loa.source === 'signed' && loa.signed_by_name) {
    return `Signed in Roger by ${loa.signed_by_name} · ${date}`;
  }
  return [loa.filename, date].filter(Boolean).join(' · ');
};

const invoiceLine = (detail: PortOrderDetail): string => {
  const { invoice, loa } = detail.documents;
  if (!invoice.attached) {
    return loa.attached
      ? 'Not attached. Your signed letter is sent in its place.'
      : 'Not attached. Optional: your signed letter is sent in its place once attached.';
  }
  const date = formatDisplayDateInAgencyZone(invoice.attached_at);
  if (invoice.source === 'loa') {
    return `${loa.filename ?? invoice.filename ?? 'Your letter'} sent in its place · ${date}`;
  }
  const pending = detail.invoice_differences.filter(
    difference => difference.resolution === 'pending'
  ).length;
  return [
    invoice.filename,
    date,
    pending > 0 ? `${pending} difference${pending === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * The letter of authorization and the optional invoice. Both download at any
 * status; while the order is a draft they can be replaced or removed, and the
 * letter can be signed in Roger instead of by hand.
 */
const PortDocuments: React.FC<{
  detail: PortOrderDetail;
  editable: boolean;
  onChanged: (detail: PortOrderDetail) => void;
  showSuccess: Notify;
  showError: Notify;
}> = ({ detail, editable, onChanged, showSuccess, showError }) => {
  const [busy, setBusy] = useState<Kind | 'template' | null>(null);
  const [removing, setRemoving] = useState<Kind | null>(null);
  const [isRemoveRunning, setIsRemoveRunning] = useState(false);
  const [isSignOpen, setIsSignOpen] = useState(false);

  const handleTemplate = async () => {
    setBusy('template');
    try {
      const response = await getLoaTemplate({
        path: { port_order_id: detail.id },
        throwOnError: true,
      });
      downloadResponseAsFile(response, 'loa-template.pdf');
    } catch (error) {
      logFailure('Failed to download LOA template', error);
      showError(
        await describeDownloadError(error, 'Unable to download the letter.')
      );
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async (kind: Kind) => {
    setBusy(kind);
    try {
      const response = await downloadPortDocument({
        path: { port_order_id: detail.id, document_kind: kind },
        throwOnError: true,
      });
      downloadResponseAsFile(
        response,
        detail.documents[kind].filename ?? `${kind}.pdf`
      );
    } catch (error) {
      logFailure('Failed to download port document', error);
      showError(
        await describeDownloadError(error, 'Unable to download the document.')
      );
    } finally {
      setBusy(null);
    }
  };

  const handleUpload = async (kind: Kind, file: File) => {
    const problem = pdfProblem(file);
    if (problem) {
      showError(problem);
      return;
    }
    setBusy(kind);
    try {
      const { data } = await uploadPortDocument({
        path: { port_order_id: detail.id, document_kind: kind },
        body: { file },
        throwOnError: true,
      });
      showSuccess('Document attached');
      onChanged(data);
    } catch (error) {
      logFailure('Failed to upload port document', error);
      showError(describeError(error, 'Unable to attach the document.'));
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async () => {
    if (!removing) return;
    setIsRemoveRunning(true);
    try {
      const { data } = await removePortDocument({
        path: { port_order_id: detail.id, document_kind: removing },
        throwOnError: true,
      });
      showSuccess('Document removed');
      setRemoving(null);
      onChanged(data);
    } catch (error) {
      logFailure('Failed to remove port document', error);
      showError(describeError(error, 'Unable to remove the document.'));
    } finally {
      setIsRemoveRunning(false);
    }
  };

  const row = (kind: Kind) => {
    const info = detail.documents[kind];
    const isLoaCopy = kind === 'invoice' && info.source === 'loa';
    const Icon = info.attached ? FileText : Paperclip;
    const line = kind === 'loa' ? loaLine(detail, info) : invoiceLine(detail);
    const pickLabel = kind === 'loa' ? 'Signed letter PDF' : 'Invoice PDF';
    return (
      <li
        key={kind}
        className='flex flex-col gap-3 rounded-md border p-3 text-sm sm:flex-row sm:items-center'
      >
        <div className='flex min-w-0 flex-1 items-start gap-3'>
          <Icon className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
          <div className='flex min-w-0 flex-col'>
            <div className='flex flex-wrap items-center gap-2'>
              <span className='font-medium'>{LABELS[kind]}</span>
              {kind === 'invoice' && detail.status === 'draft' && (
                <Badge variant='neutral' className='whitespace-nowrap'>
                  Optional
                </Badge>
              )}
            </div>
            <span className='break-words text-xs text-muted-foreground'>
              {line}
            </span>
          </div>
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          {busy === kind && <Loader2 className='h-4 w-4 animate-spin' />}
          {info.attached && (
            <Button
              variant='outline'
              size='sm'
              disabled={busy !== null}
              onClick={() => void handleDownload(kind)}
              aria-label={`Download ${LABELS[kind].toLowerCase()}`}
            >
              <Download className='mr-2 h-4 w-4' />
              Download
            </Button>
          )}
          {editable && (!info.attached || isLoaCopy) && (
            <PdfFileButton
              inputLabel={pickLabel}
              disabled={busy !== null}
              onFile={file => void handleUpload(kind, file)}
            >
              <Upload className='mr-2 h-4 w-4' />
              {kind === 'loa' ? 'Upload signed letter' : 'Upload invoice'}
            </PdfFileButton>
          )}
          {editable && info.attached && !isLoaCopy && (
            <>
              <PdfFileButton
                inputLabel={`Replacement ${pickLabel}`}
                disabled={busy !== null}
                onFile={file => void handleUpload(kind, file)}
              >
                Replace
              </PdfFileButton>
              <Button
                variant='ghost'
                size='sm'
                disabled={busy !== null}
                onClick={() => setRemoving(kind)}
                aria-label={`Remove ${LABELS[kind].toLowerCase()}`}
              >
                <Trash2 className='mr-2 h-4 w-4' />
                Remove
              </Button>
            </>
          )}
        </div>
      </li>
    );
  };

  return (
    <section className='space-y-3'>
      <h4 className='text-sm font-semibold'>Documents</h4>
      {editable && (
        <div className='flex flex-wrap gap-2'>
          <Button size='sm' onClick={() => setIsSignOpen(true)}>
            Sign letter in Roger
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => void handleTemplate()}
            disabled={busy === 'template'}
          >
            {busy === 'template' ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <Download className='mr-2 h-4 w-4' />
            )}
            Download to sign by hand
          </Button>
        </div>
      )}
      <ul className='space-y-2'>
        {row('loa')}
        {row('invoice')}
      </ul>
      {editable && (
        <p className='text-xs text-muted-foreground'>
          You can replace or remove documents until you submit.
        </p>
      )}

      {isSignOpen && (
        <SignLoaDialog
          detail={detail}
          onClose={() => setIsSignOpen(false)}
          onSigned={onChanged}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}

      <ConfirmationDialog
        open={removing !== null}
        title={
          removing ? `Remove ${LABELS[removing].toLowerCase()}?` : 'Remove?'
        }
        message={removing ? REMOVE_MESSAGES[removing] : ''}
        confirmLabel='Remove'
        destructive
        loading={isRemoveRunning}
        onAccept={() => void handleRemove()}
        onCancel={() => setRemoving(null)}
      />
    </section>
  );
};

export default PortDocuments;
